/**
 * AI Lab → MCP Builder (A-0008.07).
 *
 * Mirrors the ApiManager UX shape: left list + right detail/form/test pane.
 * Lists, creates, edits, deletes MCP connections (Project O realm L1) and
 * exposes a Test dialog that calls Mind config-mode /v1/mcp/discover and
 * /v1/mcp/invoke via the BeX-server proxy.
 *
 * Naming principle (A-0008.07): protocol-specific naming below the opcode-step
 * abstraction. Each tool protocol gets its own AI Lab builder.
 */
import {
    useContext, useEffect, useState
} from 'react';
import {
    Card, CardContent
} from 'shared/components/ui/card';
import {
    Button
} from 'shared/components/ui/button';
import {
    OrganizationContext
} from 'shared/context/OrganizationContext';
import {
    SubOrgContext
} from 'shared/context/SubOrganizationContext';
import {
    mcpConnectionService
} from 'shared/api/services/BEX/mcpConnection.service';
import MCPList from './MCPList';
import MCPForm from './MCPForm';
import TestMCPDialog from './TestMCPDialog';

const EMPTY_FORM = {
    connection_name: '',
    description: '',
    server_url: '',
    transport: 'streamable_http',
    timeout_ms: 30000,
    auth: {
        type: 'none'
    },
    headers: {},
};

const MCPManager = () => {
    const {
        selectedOrganization
    } = useContext(OrganizationContext);
    const {
        selectedSubOrganization
    } = useContext(SubOrgContext);
    const orgId = selectedOrganization?.id || selectedOrganization?._id;
    const subOrgId = selectedSubOrganization?.id || selectedSubOrganization?._id;

    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Selection / mode state.
    const [mode, setMode] = useState('idle'); // 'idle' | 'create' | 'edit'
    const [selectedId, setSelectedId] = useState(null);
    const [formDraft, setFormDraft] = useState(EMPTY_FORM);
    const [testDialogOpen, setTestDialogOpen] = useState(false);

    const refresh = async () => {
        if (!orgId || !subOrgId) return;
        setLoading(true);
        setError('');
        try {
            const list = await mcpConnectionService.listMCPConnections(orgId, subOrgId);
            setConnections(list || []);
        } catch (err) {
            setError(err?.message || String(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, [orgId, subOrgId]);

    const startCreate = () => {
        setMode('create');
        setSelectedId(null);
        setFormDraft(EMPTY_FORM);
    };

    const startEdit = (conn) => {
        setMode('edit');
        setSelectedId(conn.connection_id);
        setFormDraft({
            connection_name: conn.connection_name || '',
            description: conn.description || '',
            server_url: conn.server_url || '',
            transport: conn.transport || 'streamable_http',
            timeout_ms: conn.timeout_ms || 30000,
            auth: conn.auth || {
                type: 'none'
            },
            headers: conn.headers || {},
        });
    };

    const cancel = () => {
        setMode('idle');
        setSelectedId(null);
        setFormDraft(EMPTY_FORM);
    };

    const handleSaved = async () => {
        await refresh();
        cancel();
    };

    const handleDelete = async (conn) => {
        if (!window.confirm(`Delete MCP connection "${conn.connection_name}"?`)) return;
        try {
            await mcpConnectionService.deleteMCPConnection(orgId, subOrgId, conn.connection_id);
            await refresh();
            if (selectedId === conn.connection_id) cancel();
        } catch (err) {
            setError(err?.message || String(err));
        }
    };

    const selectedConnection = connections.find((c) => c.connection_id === selectedId);

    if (!orgId || !subOrgId) {
        return (
            <div className="p-6">
                <p className="text-sm text-muted-foreground">
                    Select an organization and sub-organization to manage MCP connections.
                </p>
            </div>
        );
    }

    return (
        <div className="p-4 grid gap-4" style={{
            gridTemplateColumns: 'minmax(280px, 360px) 1fr'
        }}>
            {/* Left column — list */}
            <Card>
                <CardContent className="p-3 space-y-3">
                    <div className="flex items-center justify-between">
                        <h2 className="font-semibold">MCP Connections</h2>
                        <Button size="sm" onClick={startCreate}>+ New</Button>
                    </div>
                    {error && <p className="text-xs text-destructive">{error}</p>}
                    <MCPList
                        connections={connections}
                        loading={loading}
                        selectedId={selectedId}
                        onEdit={startEdit}
                        onDelete={handleDelete}
                    />
                </CardContent>
            </Card>

            {/* Right column — form / detail */}
            <Card>
                <CardContent className="p-4">
                    {mode === 'idle' && !selectedConnection && (
                        <div className="text-sm text-muted-foreground">
                            Select a connection from the left to edit, or click "New" to create one.
                        </div>
                    )}
                    {(mode === 'create' || mode === 'edit') && (
                        <MCPForm
                            mode={mode}
                            orgId={orgId}
                            subOrgId={subOrgId}
                            connectionId={selectedId}
                            existing={selectedConnection}
                            draft={formDraft}
                            onDraftChange={setFormDraft}
                            onSaved={handleSaved}
                            onCancel={cancel}
                            onTest={() => setTestDialogOpen(true)}
                        />
                    )}
                </CardContent>
            </Card>

            {/* Test dialog */}
            <TestMCPDialog
                open={testDialogOpen}
                onClose={() => setTestDialogOpen(false)}
                orgId={orgId}
                subOrgId={subOrgId}
                connectionId={selectedId}
                draft={formDraft}
                isSaved={mode === 'edit'}
            />
        </div>
    );
};

export default MCPManager;
